import {
   Demand,
   DemandArea,
   DemandStatus,
   DemandSubType,
   DemandType,
   QuestionDTO,
   QuestionStatus,
   SubscriptionLevel,
} from '../api/schemas/schema'
import i18n, { t } from '../i18n/i18n'
import { enumValuesToTranslate } from './enumHelper'
import { matchesAnyField, normalizeForSearch, SearchField } from './searchHelper'
import { searchableText } from './textHelper'

export const MIN_SEARCH_LENGTH = 2

export type DemandSearchHit =
   | { kind: 'demand'; demand: Demand; parent?: Demand }
   | { kind: 'question'; question: QuestionDTO; demand: Demand }

type FlatDemand = { demand: Demand; parent?: Demand }

/** Depth-first, document order. Deliberately unfiltered by status — search spans every tab. */
export const flattenDemands = (demands: Demand[]): FlatDemand[] => {
   const flat: FlatDemand[] = []
   const walk = (items: Demand[], parent?: Demand) => {
      items?.forEach((demand) => {
         flat.push({ demand, parent })
         if (demand.children?.length) walk(demand.children, demand)
      })
   }
   walk(demands)
   return flat
}

const bodyTextField = (html: string): SearchField => {
   const { text, lower } = searchableText(html)
   return { value: text, normalized: lower }
}

/** Mirrors what the columns render, so enums match on their translated label, not their numeric value. */
export const demandSearchFields = (demand: Demand): SearchField[] => [
   { value: `D${demand.level}` },
   { value: demand.publicId },
   { value: demand.designation },
   bodyTextField(demand.text),
   { value: enumValuesToTranslate(DemandType, demand.type) },
   { value: enumValuesToTranslate(DemandSubType, demand.subType) },
   { value: demand.source },
   { value: enumValuesToTranslate(DemandArea, demand.area) },
   { value: enumValuesToTranslate(DemandStatus, demand.status) },
]

export const questionSearchFields = (question: QuestionDTO, demand: Demand): SearchField[] => [
   { value: t('Question') },
   { value: `${demand.publicId}.${question.publicId}` },
   { value: question.designation },
   bodyTextField(question.text),
   { value: question.subscriptions?.map((sConn) => enumValuesToTranslate(SubscriptionLevel, sConn.subscriptionLevel)).join(' ') },
   { value: enumValuesToTranslate(QuestionStatus, question.status) },
]

/**
 * Pre-normalized so a query costs one String.includes per field. Keyed on the record object, not its
 * id, so a reloaded tree's stale entries are collected rather than invalidated. Enum labels are baked
 * in, hence the language guard.
 */
type CachedFields = { language: string; fields: SearchField[] }

const demandFieldCache = new WeakMap<Demand, CachedFields>()
const questionFieldCache = new WeakMap<QuestionDTO, CachedFields>()

const normalized = (fields: SearchField[]): SearchField[] =>
   fields.map((field) => ({ value: field.value, normalized: field.normalized ?? normalizeForSearch(field.value) }))

const cachedFields = <T extends object>(cache: WeakMap<T, CachedFields>, record: T, build: () => SearchField[]): SearchField[] => {
   const cached = cache.get(record)
   if (cached && cached.language === i18n.language) return cached.fields

   const fields = normalized(build())
   cache.set(record, { language: i18n.language, fields })
   return fields
}

/**
 * Questions come from demand.questions, already nested by demandList(). Fetching the flat
 * QuestionDTO list instead took ~124 s for 1785 questions against ~250 ms of matching; its only
 * gain was designation, which the nested type omits. That field is still listed above, so search
 * picks it up by itself if the API ever nests it.
 */
export const searchDemandsAndQuestions = (demands: Demand[], query: string): DemandSearchHit[] => {
   const normalizedQuery = normalizeForSearch(query.trim())
   if (!normalizedQuery) return []

   const hits: DemandSearchHit[] = []

   flattenDemands(demands).forEach(({ demand, parent }) => {
      if (matchesAnyField(cachedFields(demandFieldCache, demand, () => demandSearchFields(demand)), normalizedQuery)) {
         hits.push({ kind: 'demand', demand, parent })
      }
      demand.questions
         ?.filter((question) =>
            matchesAnyField(cachedFields(questionFieldCache, question, () => questionSearchFields(question, demand)), normalizedQuery)
         )
         .forEach((question) => hits.push({ kind: 'question', question, demand }))
   })

   return hits
}
